import { LngLatBounds, Marker } from 'maplibre-gl';
import ServiceProvider from '../markers/ServiceProvider';
import ServiceProviderServiceJob from '../markers/ServiceProviderServiceJob';

class MapLib {
  constructor({ map, zoom, speed, theme, onServiceProviderClick }) {
    this.map = map;
    this.unHighlightedMarkerOpacity = 0.5;
    this.highlightedMarkerOpacity = 1;
    this.zoom = zoom;
    this.speed = speed;
    this.theme = theme;
    this.marker = {
      job: null,
      serviceProviders: [],
      selectedServiceProvider: null,
    };
    this.onServiceProviderClick = onServiceProviderClick;
    this.serviceProviders = [];
  }

  jumpTo(lat, long) {
    let centerTo = { speed: this.speed, zoom: this.zoom, essential: true };
    centerTo = {
      ...centerTo,
      center: [long, lat],
    };
    this.map.jumpTo(centerTo);
  }

  setJob({ job }) {
    this.job = job;
  }

  setServiceProviders({ serviceProviders }) {
    this.cleanSp();
    this.serviceProviders = serviceProviders;
  }

  renderJob() {
    this.serviceJobApplyToMapAsMarker();
  }

  renderServiceProviders() {
    if (this.serviceProviders.length > 0) {
      this.serviceProviderApplyToMapAsMarkers();
    }
  }

  getAllSPCoordinates() {
    const coordinates = [];
    this.serviceProviders.forEach(sp => {
      sp.addresses.forEach(spAddress => {
        coordinates.push([spAddress.locationLng, spAddress.locationLat]);
      });
      sp.assignedServiceJobs.forEach(assignedServiceJob => {
        coordinates.push([
          assignedServiceJob.locationLng,
          assignedServiceJob.locationLat,
        ]);
      });
    });
    return coordinates;
  }

  removeAllServiceProviderMarkers() {
    this.marker?.serviceProviders?.forEach(marker => {
      if (marker) {
        marker.remove();
      }
    });
    this.marker.serviceProviders = [];
  }

  cleanSp() {
    this.removeAllServiceProviderMarkers();
    this.serviceProviders = [];
    this.marker = {
      job: null,
      serviceProviders: [],
      selectedServiceProvider: null,
    };
  }

  static coreCreateLayer({
    layerId,
    sourceName,
    markerName,
    layoutProps = {},
    paintProps = {},
  }) {
    return {
      id: layerId,
      type: 'symbol',
      source: sourceName,
      layout: {
        'icon-image': markerName,
        ...layoutProps,
      },
      paint: {
        ...paintProps,
      },
    };
  }

  coreShowLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'visible');
  }

  coreHideLayer({ name }) {
    this.map.setLayoutProperty(name, 'visibility', 'none');
  }

  static coreCreateSource({ features }) {
    return {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features,
      },
    };
  }

  static coreSvgObjectToImageSrc(svgObj) {
    const xmlSerializer = new XMLSerializer();
    const svgString = xmlSerializer.serializeToString(svgObj);
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
  }

  serviceJobApplyToMapAsMarker() {
    const CENTER_TO = 'SERVICE_JOB'; // or "ALL_LOCATIONS"
    let centerTo = { speed: this.speed, zoom: this.zoom, essential: true };

    this.marker.job = new Marker({ color: 'red' })
      .setLngLat([this.job.locationLng, this.job.locationLat])
      .addTo(this.map);

    if (CENTER_TO === 'SERVICE_JOB') {
      centerTo = {
        ...centerTo,
        center: [this.job.locationLng, this.job.locationLat],
      };
    } else {
      const bounds = new LngLatBounds();
      const coordinates = this.getAllSPCoordinates();
      if (!coordinates || coordinates.length === 0) return;
      coordinates.forEach(([lng, lat]) => bounds.extend([lng, lat]));
      const boundsCenter = bounds.getCenter();
      centerTo = {
        ...centerTo,
        center: [boundsCenter.lng, boundsCenter.lat],
      };
    }

    this.map.jumpTo(centerTo);
  }

  static serviceProviderGetMarkerId(serviceProviderId, spAddressId) {
    return `SP_${serviceProviderId}${spAddressId}`;
  }

  static serviceProviderToggleMarker(serviceProviderId, addresses, show) {
    addresses.forEach(spAddress => {
      if (!spAddress?.id) return;
      const marker = document.getElementById(
        MapLib.serviceProviderGetMarkerId(serviceProviderId, spAddress.id),
      );
      if (marker) {
        marker.style.display = show ? '' : 'none';
      }
    });
  }

  serviceProviderServiceJobToggleMarker(checked, index) {
    const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
    if (checked) {
      this.coreShowLayer({ name: layerName });
    } else {
      this.coreHideLayer({ name: layerName });
    }
  }

  hideAllServiceProviderMarkers() {
    if (!this.serviceProviders) return;

    this.serviceProviders.forEach(sp => {
      sp.addresses.forEach(spAddress => {
        const marker = document.getElementById(
          MapLib.serviceProviderGetMarkerId(sp.id, spAddress.id),
        );
        if (marker) {
          marker.style.display = 'none';
        }
      });
    });

    this.serviceProviders.forEach((sp, index) => {
      const layerName = MapLib.serviceProviderServiceJobGetLayerName(index);
      this.coreHideLayer({ name: layerName });
    });
  }

  static serviceProviderHighlightMarker(serviceProviderId, spAddressId) {
    const marker = document.getElementById(
      MapLib.serviceProviderGetMarkerId(serviceProviderId, spAddressId),
    );
    marker.getElementById('outer').setAttribute('display', 'block');
  }

  static serviceProviderUnHighlightMarker(serviceProviderId, spAddressId) {
    const marker = document.getElementById(
      MapLib.serviceProviderGetMarkerId(serviceProviderId, spAddressId),
    );
    marker.getElementById('outer').setAttribute('display', 'none');
  }

  serviceProviderUnHighlightAllMarkers(ignoreServiceProviders = []) {
    this.serviceProviders.every(sp => {
      if (!ignoreServiceProviders.includes(sp.id)) {
        sp.addresses.forEach(spAddress => {
          MapLib.serviceProviderUnHighlightMarker(sp.id, spAddress.id);
        });
      }
      return true;
    });
  }

  async serviceProviderApplyToMapAsMarkers() {
    await this.serviceProviders.forEach(async (sp, index) => {
      sp.addresses.forEach(async spAddress => {
        const markerElement = ServiceProvider({
          onClickHandler: this.onServiceProviderClick,
          serviceProvider: sp,
          theme: this.theme,
          index,
          id: MapLib.serviceProviderGetMarkerId(sp.id, spAddress.id),
          mapObj: this.map,
          contact: sp?.contacts?.find(
            c => c.locationId === spAddress.locationId,
          ),
          spAddress,
        });
        this.marker.serviceProviders.push(
          new Marker({
            element: markerElement,
          })
            .setLngLat([spAddress.locationLng, spAddress.locationLat])
            .addTo(this.map),
        );
      });
      // Apply the assigned jobs only once for each SP, assigned jobs are not by addresses as yet
      await this.serviceProviderServiceJobApplyToMapAsLayer({
        serviceProvider: sp,
        index,
      });
    });
  }

  static serviceProviderServiceJobGetLayerName(index) {
    return `spServiceJobLayer${index}`;
  }

  serviceProviderServiceJobIsHighlightedLayer(layerName) {
    return (
      this.map.getPaintProperty(layerName, 'icon-opacity') ===
      this.highlightedMarkerOpacity
    );
  }

  serviceProviderServiceJobHighlightLayer(layerName) {
    this.map.setPaintProperty(
      layerName,
      'icon-opacity',
      this.highlightedMarkerOpacity,
    );
  }

  serviceProviderServiceJobUnHighlightLayer(layerName) {
    this.map.setPaintProperty(
      layerName,
      'icon-opacity',
      this.unHighlightedMarkerOpacity,
    );
  }

  serviceProviderServiceJobUnHighlightAllLayers(ignoreServiceProviders) {
    this.serviceProviders.every((sp, index) => {
      if (!ignoreServiceProviders.includes(sp.id)) {
        this.serviceProviderServiceJobUnHighlightLayer(
          MapLib.serviceProviderServiceJobGetLayerName(index),
        );
      }
      return true;
    });
  }

  async serviceProviderServiceJobApplyToMapAsLayer({ index, serviceProvider }) {
    const SOURCE_NAME = `serviceProviderSource${index}`;
    const MARKER_NAME = `serviceProviderMarkers${index}`;
    const LAYER_ID = MapLib.serviceProviderServiceJobGetLayerName(index);

    const features = MapLib.serviceProviderServiceJobGetFeatures({
      index,
      layerId: LAYER_ID,
      serviceProvider,
    });

    const source = MapLib.coreCreateSource({
      sourceName: SOURCE_NAME,
      features,
    });

    if (!this.map.hasImage(MARKER_NAME)) {
      const svgMarker = ServiceProviderServiceJob({
        text: index + 1,
      });

      const svgImage = new Image(25, 25);
      svgImage.src = MapLib.coreSvgObjectToImageSrc(svgMarker);
      svgImage.onload = () => {
        this.map.addImage(MARKER_NAME, svgImage);
      };
    } else {
      this.map.removeLayer(LAYER_ID);
      this.map.removeSource(SOURCE_NAME);
    }

    this.map.addSource(SOURCE_NAME, source);

    this.map.addLayer(
      MapLib.coreCreateLayer({
        layerId: LAYER_ID,
        sourceName: SOURCE_NAME,
        markerName: MARKER_NAME,
        paintProps: {
          'icon-opacity': this.unHighlightedMarkerOpacity,
        },
      }),
    );

    return this.map;
  }

  static serviceProviderServiceJobGetFeatures({
    serviceProvider,
    index,
    layerId,
  }) {
    return serviceProvider?.assignedServiceJobs?.map(assignedServiceJob => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          assignedServiceJob.locationLng,
          assignedServiceJob.locationLat,
        ],
      },
      properties: {
        serviceProviderId: serviceProvider.id,
        index,
        layerId,
      },
    }));
  }

  serviceProviderServiceJobOnClick(e) {
    const { serviceProviderId, layerId } = e.features[0].properties;
    const serviceProviders = MapLib.jobInfo?.current?.serviceProviders;
    // Deselect any previous layers
    this.serviceProviderUnHighlightAllMarkers([serviceProviderId]);
    this.serviceProviderServiceJobUnHighlightAllLayers(serviceProviders, [
      serviceProviderId,
    ]);
    // Select the clicked layer
    if (this.serviceProviderServiceJobIsHighlightedLayer(layerId)) {
      MapLib.serviceProviderUnHighlightMarker(serviceProviderId);
      this.serviceProviderServiceJobUnHighlightLayer(layerId);
    } else {
      MapLib.serviceProviderHighlightMarker(serviceProviderId);
      this.serviceProviderServiceJobHighlightLayer(layerId);
    }
  }

  /** Below code is unused but might be needed when we have a ton of SP to be displayed */
  /** ------------------------------------ start ------------------------------------ */
  async serviceProviderApplyToMapAsLayer({ serviceProviders, markerUrl }) {
    const SOURCE_NAME = 'serviceProviderSource';
    const MARKER_NAME = 'serviceProviderMarkers';
    const LAYER_ID = 'serviceProvidersLayer';

    const features = this.serviceProviderGetFeatures({ serviceProviders });

    const source = this.coreCreateSource({
      sourceName: SOURCE_NAME,
      features,
    });

    if (!this.map.hasImage(MARKER_NAME)) {
      const image = await this.map.loadImage(markerUrl);
      this.map.addImage(MARKER_NAME, image.data);
    } else {
      this.map.removeLayer(LAYER_ID);
      this.map.removeSource(SOURCE_NAME);
    }

    this.map.addSource(SOURCE_NAME, source);

    this.map.addLayer(
      this.coreCreateLayer({
        layerId: LAYER_ID,
        sourceName: SOURCE_NAME,
        markerName: MARKER_NAME,
      }),
    );

    return this.map;
  }

  serviceProviderGetFeatures() {
    return this.serviceProviders?.map(serviceProvider => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [serviceProvider.locationLng, serviceProvider.locationLng],
      },
      properties: {
        serviceProviderId: serviceProvider.id,
      },
    }));
  }
  /** ------------------------------------ end ------------------------------------ */
}

export default MapLib;
